import { StatusData, ITenant, dollars, dollarsToNum, errorToString, LoanCalculations, LoanApplication } from "@oneethos/shared"
import { useEffect, useRef } from "react"
import api from "../api-client"
import { toast } from "react-toastify"
import LoanOptionCard from "./loan-option-card"
import { TenantState } from "../reducers/tenantReducer"
import WaveDotsLoader from "./loader"

type SelectedLoanTermProps = {
  project: StatusData
  tenant: TenantState
  onChange: (project: StatusData) => void
  hideDisclaimer?: boolean
}

export const SelectedLoanTerm = ({
  project,
  tenant,
  onChange,
  hideDisclaimer = false
}: SelectedLoanTermProps) => {
  const pollingInterval = useRef(null)

  const fetchData = () => {
    api.getProject(project.lookup).then(data => {
      onChange(data)
    }).catch(ex => toast.error(errorToString(ex), { autoClose: false }))
  }

  useEffect(() => {
    if (!project.productId && !pollingInterval.current) {
      pollingInterval.current = setInterval(fetchData, 10_000)
    } else {
      clearInterval(pollingInterval.current)
    }

    return () => clearInterval(pollingInterval.current)
  }, [project.productId])


  if (!project.productId) {
    return <div className="awaiting-loan-term alert alert-info">
      <div className="loader-wrap">
        <WaveDotsLoader dotCount={4} duration={10} />
      </div>
      <div className="alt-label">Waiting for customer to select a loan term</div>
    </div>
  }

  const product = tenant.config.products.find(p => p.id === project.productId)

  if (!product) {
    return <div className="alert alert-warning">
      Could not find product, cannot display loan payment information.
    </div>
  }

  const payments = LoanCalculations.paymentsForProduct(
    product,
    project.loanAmount,
    project.installationAddress,
    project.projectType,
    project.loanModFee || 0
  )

  const apr = LoanCalculations.apr(
    dollarsToNum(project.loanAmount),
    product.term,
    payments.withAutoPay
  )

  return <div className="form-group loan-options">
    <LoanOptionCard
      financingTenant={tenant.config as ITenant}
      incentive={project.incentive}
      loanTermYears={LoanCalculations.calculateLoanTermInYears(product.term)}
      interestWithAutoPay={product.interestRateWithAutopay}
      estimatedPaymentWithAutopay={dollars(payments.withAutoPay)}
      estimatedPaymentWithOutAutopay={dollars(payments.withoutAutoPay)}
      isConsumerView={false}
      isSelected={product.id === project.productId}
      loanTermMonths={product.term}
      interestOnlyPeriod={product.interestOnlyPeriod}
      apr={apr}
      isResidential={project.projectType === "residential"}
    />
    {!hideDisclaimer && project.projectType === "residential" && (
      <p className="mt-3 fst-italic small">
        *{tenant.config.name} offers a discount to clients that opt into, and maintain,
        automatic monthly payments. The bill amount will automatically be withdrawn
        from the client's checking account.
      </p>
    )}
  </div>
}

export default SelectedLoanTerm